import { useCallback, useEffect, useRef, useState } from 'react';

const FAB_SIZE_PX = 56;
const EDGE_MARGIN_PX = 24;
const VIEWPORT_MARGIN_PX = 8;
const DRAG_THRESHOLD_PX = 6;
const POSITION_STORAGE_KEY = 'toothreferral-support-fab-position';

type FabPosition = {
  left: number;
  top: number;
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  startLeft: number;
  startTop: number;
  hasDragged: boolean;
};

type SupportFabProps = {
  onClick: () => void;
};

function getDefaultPosition(): FabPosition {
  if (typeof window === 'undefined') {
    return { left: 0, top: 0 };
  }

  return {
    left: window.innerWidth - FAB_SIZE_PX - EDGE_MARGIN_PX,
    top: window.innerHeight - FAB_SIZE_PX - EDGE_MARGIN_PX,
  };
}

function clampPosition(position: FabPosition): FabPosition {
  if (typeof window === 'undefined') {
    return position;
  }

  const maxLeft = window.innerWidth - FAB_SIZE_PX - VIEWPORT_MARGIN_PX;
  const maxTop = window.innerHeight - FAB_SIZE_PX - VIEWPORT_MARGIN_PX;

  return {
    left: Math.max(VIEWPORT_MARGIN_PX, Math.min(position.left, maxLeft)),
    top: Math.max(VIEWPORT_MARGIN_PX, Math.min(position.top, maxTop)),
  };
}

function loadStoredPosition(): FabPosition | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<FabPosition>;
    if (typeof parsed.left !== 'number' || typeof parsed.top !== 'number') {
      return null;
    }

    return clampPosition({ left: parsed.left, top: parsed.top });
  } catch {
    return null;
  }
}

function savePosition(position: FabPosition): void {
  try {
    window.localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Ignore storage failures (private browsing, quota exceeded, etc.)
  }
}

export function SupportFab({ onClick }: SupportFabProps) {
  // `null` means "not yet dragged" — render at the bottom-right corner purely via
  // CSS so the server and the first client render agree. Only once the user drags
  // (or a previously dragged position is loaded) do we switch to absolute left/top.
  // This avoids the top-left {0,0} the server would otherwise emit from a
  // window-derived default, which could get stuck on a hydration hiccup.
  const [position, setPosition] = useState<FabPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<DragState | null>(null);

  useEffect(() => {
    const stored = loadStoredPosition();
    if (stored) {
      setPosition(stored);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setPosition(current => (current ? clampPosition(current) : current));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      // When still on the default CSS anchor, seed the drag from the current
      // on-screen (bottom-right) coordinates so dragging starts smoothly.
      const base = position ?? getDefaultPosition();
      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startLeft: base.left,
        startTop: base.top,
        hasDragged: false,
      };
    },
    [position]
  );

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;

    if (
      !dragState.hasDragged &&
      (Math.abs(deltaX) > DRAG_THRESHOLD_PX || Math.abs(deltaY) > DRAG_THRESHOLD_PX)
    ) {
      dragState.hasDragged = true;
      setIsDragging(true);
    }

    if (!dragState.hasDragged) {
      return;
    }

    setPosition(
      clampPosition({
        left: dragState.startLeft + deltaX,
        top: dragState.startTop + deltaY,
      })
    );
  }, []);

  const finishPointerInteraction = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      if (dragState.hasDragged) {
        const finalPosition = clampPosition({
          left: dragState.startLeft + (event.clientX - dragState.startX),
          top: dragState.startTop + (event.clientY - dragState.startY),
        });
        setPosition(finalPosition);
        savePosition(finalPosition);
      } else {
        onClick();
      }

      dragStateRef.current = null;
      setIsDragging(false);
    },
    [onClick]
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      finishPointerInteraction(event);
    },
    [finishPointerInteraction]
  );

  const handlePointerCancel = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragStateRef.current = null;
    setIsDragging(false);
  }, []);

  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      aria-label="Contact support"
      className={`fixed z-40 flex h-14 w-14 items-center justify-center rounded-full bg-purple text-white shadow-lg transition-colors hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
        position === null ? 'bottom-6 right-6' : ''
      } ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{
        touchAction: 'none',
        ...(position ? { left: position.left, top: position.top } : {}),
      }}
    >
      <svg
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
        />
      </svg>
    </button>
  );
}
